import { z } from 'zod';
import { AtomicAmount } from './primitives.ts';

export const CurrencyCode = z
  .string()
  .regex(/^[A-Z0-9]{2,12}$/, 'currency code must be 2-12 uppercase alphanumerics');

/** Fixed-point amount: value = amountAtomic / 10^scale units of `currency`. */
export const Money = z.object({
  amountAtomic: AtomicAmount,
  currency: CurrencyCode,
  scale: z.number().int().min(0).max(18),
});
export type Money = z.infer<typeof Money>;

export const CurrencyKind = z.enum(['FIAT', 'CRYPTO']);
export type CurrencyKind = z.infer<typeof CurrencyKind>;

export const CurrencySpec = z.object({
  /** Canonical internal asset identity (e.g. 'usdt-erc20'); a ticker alone does not identify an asset. */
  assetId: z.string().min(1).max(64),
  code: CurrencyCode,
  kind: CurrencyKind,
  /** Wire scale for this catalogue version. Not a universal ISO minor unit. */
  scale: z.number().int().min(0).max(18),
  network: z.string().min(1).max(64).optional(),
  partnerAssetCode: z.string().min(1).max(64).optional(),
  catalogueVersion: z.string().min(1).max(32),
  /** True for demo catalogue entries that do not reflect any partner contract. */
  demoOnly: z.boolean(),
});
export type CurrencySpec = z.infer<typeof CurrencySpec>;

/** Signed fixed-point amount, used only for derived figures such as net profit. */
export const SignedMoney = z.object({
  amountAtomic: z
    .string()
    .regex(/^(0|-?[1-9][0-9]*)$/, 'must be a signed base-10 integer string')
    .max(41),
  currency: CurrencyCode,
  scale: z.number().int().min(0).max(18),
});
export type SignedMoney = z.infer<typeof SignedMoney>;
